import { normalizeTerm, pickCloze, studyUnits, tokenize, type ClozeBlank } from '@engpod/core';
import { useEffect, useMemo, useState } from 'react';
import { Sentence } from '../components/Sentence';
import type { StepProps } from './context';

type Phase = 'listen' | 'cloze' | 'reveal';
type Rating = 'ok' | 'hard' | 'lost';

/** Diff the learner's dictation against the sentence, word by word. */
function compare(answer: string, target: string) {
  const want = normalizeTerm(target).split(' ');
  const got = new Set(normalizeTerm(answer).split(' '));
  return want.map((w) => ({ w, ok: got.has(w) }));
}

/**
 * 精听, one study unit at a time (tiny replies are merged with a neighbour):
 * loop the audio → tap the missing key words → read English → Chinese → rate it.
 * Typing a full dictation stays available but optional — it is slow on a phone.
 */
export function Intensive({ detail, state, update, complete, playSegment, openWord, setMode, setRate, rate, loopCount, playing }: StepProps) {
  const units = useMemo(() => studyUnits(detail.segments), [detail.segments]);
  const pool = useMemo(() => detail.segments.flatMap((s) => tokenize(s.en).flatMap((t) => (t.word ? [t.word] : []))), [detail.segments]);
  const exprWords = useMemo(() => detail.lesson.expressions.map((e) => e.text), [detail.lesson.expressions]);
  const [i, setI] = useState(() => Math.min(state.intensiveReached ?? 0, units.length - 1));
  const [phase, setPhase] = useState<Phase>('listen');
  const [loop, setLoop] = useState(true);
  const [filled, setFilled] = useState<Record<number, string>>({});
  const [wrong, setWrong] = useState<string | null>(null);
  const [showZh, setShowZh] = useState(false);
  const [dictation, setDictation] = useState<string | null>(null);
  const unit = units[i];

  const tokens = useMemo(() => (unit ? tokenize(unit.en) : []), [unit]);
  const blanks: ClozeBlank[] = useMemo(() => (unit ? pickCloze(tokens, { prefer: exprWords, pool, seed: i + 7 }) : []), [unit, tokens, exprWords, pool, i]);

  useEffect(() => setMode('none'), [setMode]);
  // Start each unit looping straight away.
  useEffect(() => {
    if (unit) playSegment({ id: unit.key, startMs: unit.startMs, endMs: unit.endMs }, loop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  if (!unit) return <p className="muted">这一集还没有字幕。</p>;
  const hard = new Set(state.hardSegments ?? []);
  const replay = (asLoop = loop) => playSegment({ id: unit.key, startMs: unit.startMs, endMs: unit.endMs }, asLoop);

  const goTo = (n: number) => {
    const next = Math.max(0, Math.min(units.length - 1, n));
    setI(next);
    setPhase('listen');
    setFilled({});
    setWrong(null);
    setShowZh(false);
    setDictation(null);
    if (next > (state.intensiveReached ?? 0)) update({ intensiveReached: next });
  };

  const rateUnit = (r: Rating) => {
    const set = new Set(hard);
    for (const id of unit.segmentIds) {
      if (r === 'ok') set.delete(id);
      else set.add(id);
    }
    update({ hardSegments: [...set] });
    if (i === units.length - 1) complete('intensive');
    else goTo(i + 1);
  };

  const pick = (blank: ClozeBlank, option: string) => {
    if (option === blank.answer) {
      const next = { ...filled, [blank.token]: option };
      setFilled(next);
      setWrong(null);
      if (blanks.every((b) => next[b.token])) setTimeout(() => setPhase('reveal'), 500);
    } else {
      setWrong(option);
      setTimeout(() => setWrong(null), 700);
    }
  };
  const activeBlank = blanks.find((b) => !filled[b.token]);

  // The word sheet works on caption sentences: map a tapped unit token back to its sentence.
  const openUnitWord = (t: number) => {
    const text = tokens[t]?.text ?? '';
    const seg = detail.segments.find((s) => unit.segmentIds.includes(s.id) && tokenize(s.en).some((x) => x.text === text)) ?? detail.segments.find((s) => s.id === unit.segmentIds[0]);
    if (!seg) return;
    openWord(seg, Math.max(0, tokenize(seg.en).findIndex((x) => x.text === text)));
  };

  return (
    <div className="step-body intensive">
      <div className="unit-progress">
        <div className="unit-bar">
          <i style={{ width: `${((i + (phase === 'reveal' ? 1 : 0.5)) / units.length) * 100}%` }} />
        </div>
        <small>
          {i + 1} / {units.length}
        </small>
      </div>

      <div className="unit-card">
        <div className="unit-top">
          <span className={`loop-badge${playing ? ' live' : ''}`}>{loop ? `循环 · 第 ${loopCount} 遍` : '单次播放'}</span>
          <div className="row tight">
            <button
              className={`chip${loop ? ' on' : ''}`}
              onClick={() => {
                setLoop(!loop);
                replay(!loop);
              }}
            >
              {loop ? '循环' : '单次'}
            </button>
            <button className={`chip${rate < 1 ? ' on' : ''}`} onClick={() => setRate(rate < 1 ? 1 : 0.75)}>
              慢速
            </button>
          </div>
        </div>

        {phase === 'listen' && (
          <button className="listen-area" onClick={() => replay()}>
            <span className="ear">🎧</span>
            <span>先只听，不看字幕</span>
            <small>点这里从头再听一遍</small>
          </button>
        )}

        {phase === 'cloze' && (
          <div className="cloze">
            <p className="en big">
              {tokens.map((t, k) => {
                const b = blanks.find((x) => x.token === k);
                if (!b) return <span key={k}>{t.text}</span>;
                return (
                  <span key={k} className={`gap${filled[k] ? ' ok' : b === activeBlank ? ' active' : ''}`}>
                    {filled[k] ?? ' '.repeat(Math.max(4, b.answer.length))}
                  </span>
                );
              })}
            </p>
            {activeBlank && (
              <div className="word-bank">
                {activeBlank.options.map((o) => (
                  <button key={o} className={`bank-word${wrong === o ? ' wrong' : ''}`} onClick={() => pick(activeBlank, o)}>
                    {o}
                  </button>
                ))}
              </div>
            )}
            <small className="muted">听到的是哪个词？点选填空，选错了可以再试。</small>
          </div>
        )}

        {phase === 'reveal' && (
          <div className="reveal">
            <p className="en big">
              <Sentence text={unit.en} onWord={openUnitWord} />
            </p>
            {showZh ? (
              <p className="zh">{unit.zh}</p>
            ) : (
              <button className="plain small show-zh" onClick={() => setShowZh(true)}>
                看中文
              </button>
            )}
            <small className="muted">点单词可查词、加入生词本</small>
          </div>
        )}

        {dictation !== null && (
          <div className="dictation">
            <textarea value={dictation} onChange={(e) => setDictation(e.target.value)} placeholder="把听到的整句写下来" rows={2} />
            {phase === 'reveal' && dictation && (
              <p className="diff">
                {compare(dictation, unit.en).map((x, k) => (
                  <span key={k} className={x.ok ? 'ok' : 'miss'}>
                    {x.w}{' '}
                  </span>
                ))}
              </p>
            )}
          </div>
        )}
      </div>

      {phase === 'listen' && (
        <div className="row wrap">
          <button className="primary grow" onClick={() => setPhase(blanks.length ? 'cloze' : 'reveal')}>
            {blanks.length ? '听好了，填词' : '听好了，看原文'}
          </button>
          <button onClick={() => setPhase('reveal')}>直接看原文</button>
        </div>
      )}
      {phase === 'cloze' && (
        <button className="plain small" onClick={() => setPhase('reveal')}>
          跳过，看原文
        </button>
      )}
      {phase === 'reveal' && (
        <div className="rate-row">
          <button className="rate lost" onClick={() => rateUnit('lost')}>
            没听懂
          </button>
          <button className="rate hard" onClick={() => rateUnit('hard')}>
            有点难
          </button>
          <button className="rate ok" onClick={() => rateUnit('ok')}>
            听懂了
          </button>
        </div>
      )}

      <div className="row spread">
        <button className="plain small" disabled={i === 0} onClick={() => goTo(i - 1)}>
          ← 上一句
        </button>
        {dictation === null ? (
          <button className="plain small" onClick={() => setDictation('')}>
            ✎ 键盘听写（可选）
          </button>
        ) : (
          <button className="plain small" onClick={() => setDictation(null)}>
            收起听写
          </button>
        )}
        <button className="plain small" onClick={() => (i === units.length - 1 ? complete('intensive') : goTo(i + 1))}>
          {i === units.length - 1 ? '完成' : '跳过 →'}
        </button>
      </div>
      <small className="muted">选「有点难」「没听懂」的句子会进入跟读练习。</small>
    </div>
  );
}
